import { useEffect, useRef, useState } from 'react'

/**
 * 自建确认框 / 输入框 —— 替代 window.confirm 与 window.prompt。
 *
 * 为什么需要：spec 里没有覆盖"删除确认框"，原生浏览器框是临时凑合的，
 * 一两处时会越积越多。形态照 monday：深色卡片 + 遮罩 + 标题 + 说明 +
 * 右下角 Cancel 与主色实底确认键（monday 的危险确认键也是蓝色，不是红色）。
 *
 * 用法：在页面里渲染一次 <DialogHost />，然后在任意地方 await：
 *   if (await askConfirm('Delete this item?', '此操作不可撤销', 'Delete')) { ... }
 *   const name = await askPrompt('分组标题', group.title)
 *
 * 提供了无宿主时的降级：直接回落原生 confirm/prompt，不会静默卡住。
 */

type DialogState =
  | {
      kind: 'confirm'
      title: string
      message: string
      confirmText: string
      resolve: (ok: boolean) => void
    }
  | {
      kind: 'prompt'
      title: string
      message: string
      confirmText: string
      initial: string
      resolve: (value: string | null) => void
    }
  | {
      kind: 'select'
      title: string
      message: string
      confirmText: string
      initial: string
      options: { value: string; label: string }[]
      resolve: (value: string | null) => void
    }

let host: ((d: DialogState) => void) | null = null

/** 确认框；返回是否点了确认键 */
export function askConfirm(title: string, message = '', confirmText = 'Delete'): Promise<boolean> {
  if (host === null) {
    // 宿主没挂上时不要静默失败
    return Promise.resolve(typeof window === 'undefined' ? false : window.confirm(message === '' ? title : message))
  }
  return new Promise<boolean>((resolve) => {
    host?.({ kind: 'confirm', title, message, confirmText, resolve })
  })
}

/** 输入框；返回输入值，取消返回 null */
export function askPrompt(title: string, initial = '', confirmText = 'OK'): Promise<string | null> {
  if (host === null) {
    return Promise.resolve(typeof window === 'undefined' ? null : window.prompt(title, initial))
  }
  return new Promise<string | null>((resolve) => {
    host?.({ kind: 'prompt', title, message: '', confirmText, initial, resolve })
  })
}

/** 下拉选择框；返回选中值，取消返回 null */
export function askSelect(
  title: string,
  message: string,
  options: { value: string; label: string }[],
  confirmText = 'OK',
): Promise<string | null> {
  if (host === null) {
    return Promise.resolve(null)
  }
  return new Promise<string | null>((resolve) => {
    host?.({
      kind: 'select',
      title,
      message,
      confirmText,
      initial: options.length > 0 ? (options[0]?.value ?? '') : '',
      options,
      resolve,
    })
  })
}

export function DialogHost() {
  const [state, setState] = useState<DialogState | null>(null)
  const [text, setText] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const okRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    host = (d) => {
      setState(d)
      setText(d.kind === 'prompt' || d.kind === 'select' ? d.initial : '')
    }
    return () => {
      host = null
    }
  }, [])

  // 打开时聚焦：输入型落在输入框，确认型落在确认键（可直接回车）
  useEffect(() => {
    if (state === null) return
    if (state.kind === 'select') {
      okRef.current?.focus()
      return
    }
    if (state.kind === 'prompt') {
      inputRef.current?.focus()
      inputRef.current?.select()
    } else {
      okRef.current?.focus()
    }
  }, [state])

  if (state === null) return null

  const close = (ok: boolean) => {
    if (state.kind === 'confirm') state.resolve(ok)
    else state.resolve(ok ? text : null)
    setState(null)
  }

  return (
    <div className="dialog-mask" role="presentation" onMouseDown={() => close(false)}>
      <div
        className="dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="dialog-title"
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Escape') close(false)
          else if (e.key === 'Enter' && state.kind === 'confirm') close(true)
        }}
      >
        <h3 id="dialog-title" className="dialog__title">
          {state.title}
        </h3>
        {state.message !== '' && <p className="dialog__msg">{state.message}</p>}

        {state.kind === 'prompt' && (
          <input
            ref={inputRef}
            className="dialog__input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') close(true)
              else if (e.key === 'Escape') close(false)
            }}
          />
        )}

        {state.kind === 'select' && (
          <select
            className="dialog__select"
            value={text}
            onChange={(e) => setText(e.target.value)}
          >
            {state.options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        )}

        <div className="dialog__actions">
          <button type="button" className="dialog__btn" onClick={() => close(false)}>取消</button>
          <button
            ref={okRef}
            type="button"
            className="dialog__btn dialog__btn--primary"
            disabled={state.kind === 'prompt' && text.trim() === ''}
            onClick={() => close(true)}
          >
            {state.confirmText}
          </button>
        </div>
      </div>
    </div>
  )
}
